import { ExportToCsv } from 'export-to-csv';
import { FidcComparatorTableItem } from 'src/services/fidcComparator';
import { convertValue } from 'src/utils/number';

export const ND = 'N/D';

type FieldValue = string | string[] | number | boolean | null | undefined;

const formatComparisonCurrencyValue = (
  value: number | string | null | undefined,
) => {
  if (value === null || value === undefined) return ND;
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return ND;

  return convertValue(numericValue, '', true, 'currency', 'BRL', 2, 2);
};

const formatComparisonDisplayValue = (value: FieldValue) => {
  if (value === null || value === undefined || value === '') return ND;
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || ND;

  return String(value);
};

export const getFidcComparisonIdentityKey = (
  item: Pick<FidcComparatorTableItem, 'operationId'>,
) => String(item.operationId);

export const buildFidcComparisonColumnLabel = (
  item: Pick<FidcComparatorTableItem, 'name' | 'fidc' | 'operationId'>,
) => item.name?.trim() || item.fidc?.trim() || String(item.operationId);

export const buildFidcComparisonItemColumnKey = (
  item: Pick<FidcComparatorTableItem, 'operationId'>,
  index: number,
) => `fidc-${item.operationId}-${index}`;

export const dedupeFidcComparisonData = (items: FidcComparatorTableItem[]) => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = getFidcComparisonIdentityKey(item);

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

interface FidcComparisonFieldDef {
  key: string;
  label: string;
  accessor: (item: FidcComparatorTableItem) => FieldValue;
  formatter?: (value: FieldValue) => string;
}

export const FIDC_COMPARISON_FIELDS: FidcComparisonFieldDef[] = [
  {
    key: 'cnpj',
    label: 'CNPJ',
    accessor: (item) => item.cnpj,
  },
  {
    key: 'pl',
    label: 'Patrimônio Líquido',
    accessor: (item) => item.pl,
    formatter: (value) =>
      formatComparisonCurrencyValue(
        value as number | string | null | undefined,
      ),
  },
  {
    key: 'dc',
    label: 'Direitos Creditórios',
    accessor: (item) => item.dc,
    formatter: (value) =>
      formatComparisonCurrencyValue(
        value as number | string | null | undefined,
      ),
  },
  {
    key: 'cvnp',
    label: 'Atrasos',
    accessor: (item) => item.cvnp,
    formatter: (value) =>
      formatComparisonCurrencyValue(
        value as number | string | null | undefined,
      ),
  },
  {
    key: 'pdd',
    label: 'Provisão para Devedores Duvidosos',
    accessor: (item) => item.pdd,
    formatter: (value) =>
      formatComparisonCurrencyValue(
        value as number | string | null | undefined,
      ),
  },
  {
    key: 'adm',
    label: 'Administrador',
    accessor: (item) => item.adm,
  },
  {
    key: 'gest',
    label: 'Gestor',
    accessor: (item) => item.gest,
  },
  {
    key: 'cust',
    label: 'Custodiante',
    accessor: (item) => item.cust,
  },
  {
    key: 'ativoLastro',
    label: 'Ativo-Lastro',
    accessor: (item) => item.ativoLastro,
  },
  {
    key: 'condominio',
    label: 'Condomínio',
    accessor: (item) => item.condominio,
  },
];

export interface FidcComparisonTableRow {
  key: string;
  label: string;
  values: Record<string, string>;
}

export const buildFidcComparisonTableRows = (
  data: FidcComparatorTableItem[],
): FidcComparisonTableRow[] =>
  FIDC_COMPARISON_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    values: data.reduce<Record<string, string>>((accumulator, item, index) => {
      const columnKey = buildFidcComparisonItemColumnKey(item, index);
      const rawValue = field.accessor(item);

      accumulator[columnKey] = field.formatter
        ? field.formatter(rawValue)
        : formatComparisonDisplayValue(rawValue);

      return accumulator;
    }, {}),
  }));

export const exportFidcComparisonRowsToCsv = (
  data: FidcComparatorTableItem[],
) => {
  const tableRows = buildFidcComparisonTableRows(data);
  const columns = [
    { key: 'label', header: 'Indicador' },
    ...data.map((item, index) => ({
      key: buildFidcComparisonItemColumnKey(item, index),
      header: buildFidcComparisonColumnLabel(item),
    })),
  ];

  const csvExporter = new ExportToCsv({
    fieldSeparator: ';',
    quoteStrings: '"',
    filename: 'comparador-risco-retorno-fidc',
    showLabels: true,
    useBom: true,
    useKeysAsHeaders: false,
    headers: columns.map((column) => column.header),
  });

  csvExporter.generateCsv(
    tableRows.map((row) => ({
      label: row.label,
      ...row.values,
    })),
  );
};
